/* appReturn — remembers the last screen a signed-in user was on inside the app,
 * so the public pages (Pricing, Terms, Privacy) can send them back there instead
 * of dumping them on the marketing landing page.
 *
 * Why storage and not router state: these pages are reached in several ways —
 * a router navigation (Upgrade plan), a brand-new tab (the Terms/Privacy links
 * in Settings open with target="_blank" rel="noopener", which starts a fresh
 * page with no history and no sessionStorage), a Stripe redirect, or a plain
 * bookmark. Storage is the only breadcrumb that survives all of them.
 *
 * Signed-out visitors are unaffected: for them "back" still means the landing
 * page, which is correct — they have no app to go back to.
 *
 * useBackToApp() reads this SYNCHRONOUSLY inside useMemo, during render —
 * @capacitor/preferences has no synchronous API, so this goes through the
 * sync-read cache in services/storage.js (same mechanism the auth token
 * uses) rather than the plain async storage.getItem/setItem.
 */

import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { readSync, writeSync } from '../services/storage'

const KEY = 'jai_last_app_path'

// Anything older than this is treated as "a different visit" and ignored, so
// nobody gets thrown back into a league page they last opened days ago.
const MAX_AGE_MS = 12 * 60 * 60 * 1000

// Where a signed-in user goes when there's nothing remembered.
export const APP_HOME = '/chat'

// The public shell: marketing, legal, auth and the Stripe return pages. These
// are the pages "back" leads away from, so they're never a return destination.
const PUBLIC_PATHS = new Set([
  '/', '/welcome', '/pricing', '/waitlist', '/terms', '/privacy',
  '/login', '/signup', '/verify', '/forgot-password', '/reset-password',
])

function isAppPath(pathname) {
  if (!pathname || !pathname.startsWith('/')) return false
  if (PUBLIC_PATHS.has(pathname)) return false
  if (pathname.startsWith('/billing')) return false
  return true
}

function rememberAppPath(fullPath) {
  writeSync(KEY, JSON.stringify({ path: fullPath, at: Date.now() }))
}

function readAppPath() {
  try {
    const raw = readSync(KEY)
    if (!raw) return null
    const { path, at } = JSON.parse(raw)
    if (!path || !isAppPath(path.split('?')[0])) return null
    if (!at || Date.now() - at > MAX_AGE_MS) return null
    return path
  } catch {
    return null
  }
}

/** Records the current location whenever it's a real in-app screen.
 *  Mounted once, inside the router (see App.jsx). */
export function useRememberAppPath() {
  const { pathname, search } = useLocation()
  useEffect(() => {
    if (isAppPath(pathname)) rememberAppPath(pathname + search)
  }, [pathname, search])
}

/** Where a "back" control on a public page should go, plus the wording for it.
 *  Signed in  → the screen they came from (or the app home).
 *  Signed out → the marketing landing page. */
export function useBackToApp() {
  const { user } = useAuth()
  return useMemo(() => (
    user
      ? { to: readAppPath() || APP_HOME, label: 'Back to app' }
      : { to: '/welcome', label: 'Back to site' }
  ), [user])
}
